// Copyright © 2022 Vewd Software AS.
//
// This file is part of Vewd Cloud,
// and includes Vewd Confidential Information.
// Distribution is strictly prohibited without Vewd's written consent.
import { languages as localesLanguages } from "@internal-node-modules/tivo-locales";

import { capitalize } from "utils/string";

interface Translation {
  language: string;
  default_translation: boolean;
  code?: string;
  name?: string;
  sections?: any;
}

export interface Language {
  type: string;
  id: string;
  code: string;
  name: string;
  eng_name: string;
  rtl?: boolean;
  iso3?: string;
}

export const getDefaultTranslationIdx = (
  translations: Translation[]
): number => {
  const idx = translations.findIndex(
    (translation) => translation.default_translation
  );

  if (idx < 0) {
    throw new Error("Cannot find default translation!");
  }

  return idx;
};

type RawLanguage = (typeof localesLanguages)[number];
const sortLanguages = (languages: RawLanguage[]): Language[] => {
  const result = languages.map(({ attributes, ...languageData }) => ({
    ...languageData,
    ...attributes,
  }));
  return result.sort((a, b) => {
    const aName = capitalize(a.name);
    const bName = capitalize(b.name);
    return aName.localeCompare(bName);
  });
};

export const cloudLanguages: Language[] = sortLanguages(localesLanguages);
export const LANGUAGE_COUNT = cloudLanguages.length;
export const languagesMap: Map<string, Language> = new Map(
  cloudLanguages.map((lang) => [lang.code, lang])
);

export const getTranslationLanguageName = (
  translation: Translation
): string => {
  const lang = languagesMap.get(translation.language);
  const langName = lang ? lang.name : "";
  return capitalize(langName);
};

export const sortTranslations = (
  translations: Translation[]
): Translation[] => {
  const result = [...translations];
  return result.sort((a, b) => {
    const aName = getTranslationLanguageName(a);
    const bName = getTranslationLanguageName(b);
    return aName.localeCompare(bName);
  });
};
